import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Text,
  Flex,
  Spinner,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Heading,
  Icon,
  SimpleGrid,
  useDisclosure,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import {
  Buildings,
  ChartPieSlice,
  CurrencyDollar,
  Users,
  TrendUp,
  MapPin,
  Briefcase,
  Receipt,
  CaretDown,
  CaretUp,
  Funnel,
  ChartBar,
  ArrowLeft,
  XCircle,
} from 'phosphor-react';
import { PageHeader } from '../../components/PageHeader';
import { BrazilMap } from '../../components/brazilMap';
import {
  useEmpresaDash,
  IEmpresaDashTotalizadores,
  IEmpresaDashPorcentagens,
} from '../../hooks/useEmpresaDash';
import { IDataNoPagination } from '../../types/shared';
import { formatCNPJ, formatCurrency } from '../../utils/FormatFunctions';
import { DateToolbarFilterImproved } from '../../components/DateToolbarFilterImproved';
import {
  StatCard,
  TaxCard,
  EmpresasListSection,
  FiltersDrawer,
  FilterType,
  PermanenciaMediaCard,
  AniversariosEmpresasCard,
} from './components';

const regimeOptions = [
  { value: 'simples_nacional', label: 'Simples Nacional' },
  { value: 'lucro_presumido', label: 'Lucro Presumido' },
  { value: 'lucro_real', label: 'Lucro Real' },
  { value: 'domesticas', label: 'Domésticas' },
  { value: 'mei', label: 'MEI' },
  { value: 'imune_isenta', label: 'Imune/Isenta' },
  { value: 'cnpj_suspenso', label: 'CNPJ Suspenso' },
  { value: 'sem_movimento', label: 'Sem Movimento' },
];

const regimeColors: Record<string, string> = {
  simples_nacional: 'green.400',
  lucro_presumido: 'blue.400',
  lucro_real: 'purple.400',
  domesticas: 'pink.400',
  mei: 'orange.400',
  imune_isenta: 'teal.400',
  cnpj_suspenso: 'red.400',
  sem_movimento: 'gray.400',
};

const regimeLabels: Record<string, string> = {
  simples_nacional: 'Simples Nacional',
  lucro_presumido: 'Lucro Presumido',
  lucro_real: 'Lucro Real',
  domesticas: 'Domésticas',
  mei: 'MEI',
  imune_isenta: 'Imune/Isenta',
  cnpj_suspenso: 'CNPJ Suspenso',
  sem_movimento: 'Sem Movimento',
};

export function EmpresaDashboardPage() {
  const { getTotalizadores, getPorcentagens } = useEmpresaDash();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const currentDate = new Date();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

  const formatDateToString = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const [filters, setFilters] = useSearchParams(
    createSearchParams({
      page: '1',
      size: '50',
      order: 'id',
      order_by: 'desc',
      date_start: formatDateToString(firstDayOfMonth),
      date_end: formatDateToString(lastDayOfMonth),
      filter_type: 'ativas',
    }),
  );

  // Estados temporários para os filtros do drawer
  const [tempEstado, setTempEstado] = useState<{ value: string; label: string } | null>(null);
  const [tempMunicipio, setTempMunicipio] = useState<{ value: string; label: string } | null>(null);
  const [tempGrupo, setTempGrupo] = useState<{ value: string; label: string } | null>(null);
  const [tempRegime, setTempRegime] = useState<{ value: string; label: string } | null>(null);

  // Estados aplicados
  const [selectedEstado, setSelectedEstado] = useState<string | null>(filters.get('uf') || null);
  const [selectedMunicipio, setSelectedMunicipio] = useState<string | null>(
    filters.get('cidade') || null,
  );
  const [selectedGrupo, setSelectedGrupo] = useState<string | null>(
    filters.get('grupo_de_empresas') || null,
  );
  const [selectedRegime, setSelectedRegime] = useState<string | null>(
    filters.get('tipo_regime') || null,
  );

  // Estados para visualização expandida (gráfico)
  const [showFaturamentoChart, setShowFaturamentoChart] = useState(false);
  const [showGruposChart, setShowGruposChart] = useState(false);
  const [showRegimeChart, setShowRegimeChart] = useState(true);

  // Estado para filtro da listagem de empresas via clique nos cards
  const [empresasFilterType, setEmpresasFilterType] = useState<FilterType>(
    (filters.get('filter_type') as FilterType) || null,
  );
  const empresasListRef = useRef<HTMLDivElement>(null);

  // Função para scroll até a seção de empresas
  const scrollToEmpresasList = () => {
    setTimeout(() => {
      if (empresasListRef.current) {
        const yOffset = -20;
        const y =
          empresasListRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  };

  // Função para filtro local da listagem (apenas scroll, sem reload)
  const handleStatCardFilter = (filterType: FilterType) => {
    setEmpresasFilterType(filterType);
    scrollToEmpresasList();
  };

  // Função para filtro global (atualiza URL e afeta totalizadores)
  // Se clicar no mesmo filtro, limpa
  const handleStatCardClick = (filterType: FilterType) => {
    const currentFilterType = filters.get('filter_type');
    const newFilters: Record<string, string> = {
      ...Object.fromEntries(filters),
    };

    if (currentFilterType === filterType) {
      // Toggle: limpa o filtro
      delete newFilters.filter_type;
      setEmpresasFilterType(null);
    } else {
      newFilters.filter_type = filterType as string;
      setEmpresasFilterType(filterType);
    }

    setFilters(createSearchParams(newFilters));
  };

  // Função para limpar filtro de tipo (ativas, inativas, etc)
  const handleClearFilterType = () => {
    setEmpresasFilterType(null);

    const newFilters: Record<string, string> = {
      ...Object.fromEntries(filters),
    };
    delete newFilters.filter_type;

    setFilters(createSearchParams(newFilters));
  };

  // Função para filtrar por regime tributário
  // Se clicar no mesmo regime, limpa
  const handleRegimeCardClick = (regimeKey: string) => {
    const currentRegime = filters.get('tipo_regime');
    const newFilters: Record<string, string> = {
      ...Object.fromEntries(filters),
    };

    if (currentRegime === regimeKey) {
      // Toggle: limpa o filtro
      delete newFilters.tipo_regime;
      setSelectedRegime(null);
      setTempRegime(null);
    } else {
      newFilters.tipo_regime = regimeKey;
      setSelectedRegime(regimeKey);
      setTempRegime(regimeOptions.find(r => r.value === regimeKey) || null);
    }

    setFilters(createSearchParams(newFilters));
  };

  // Função para limpar filtro de regime tributário
  const handleClearRegimeFilter = () => {
    const newFilters: Record<string, string> = {
      ...Object.fromEntries(filters),
    };
    delete newFilters.tipo_regime;
    setFilters(createSearchParams(newFilters));
    setSelectedRegime(null);
    setTempRegime(null);
  };

  // Função para filtrar por imposto
  // Se clicar no mesmo imposto, limpa
  const handleTaxCardClick = (taxKey: FilterType) => {
    const currentFilterType = filters.get('filter_type');
    const newFilters: Record<string, string> = {
      ...Object.fromEntries(filters),
    };

    if (currentFilterType === taxKey) {
      // Toggle: limpa o filtro
      delete newFilters.filter_type;
      setEmpresasFilterType(null);
    } else {
      newFilters.filter_type = taxKey as string;
      setEmpresasFilterType(taxKey);
    }

    setFilters(createSearchParams(newFilters));
    scrollToEmpresasList();
  };

  const filtersObject = Object.fromEntries([...Array.from(filters)]);
  const queryKeyTotalizadores = ['empresaDashTotalizadores', filtersObject];
  const queryKeyPorcentagens = ['empresaDashPorcentagens', filtersObject];

  const {
    data,
    isFetching: isFetchingTotalizadores,
    isError: isErrorTotalizadores,
  } = useQuery<IDataNoPagination<IEmpresaDashTotalizadores>>({
    queryKey: queryKeyTotalizadores,
    queryFn: getTotalizadores,
  });

  const {
    data: dataPorcentagens,
    isFetching: isFetchingPorcentagens,
    isError: isErrorPorcentagens,
  } = useQuery<IDataNoPagination<IEmpresaDashPorcentagens>>({
    queryKey: queryKeyPorcentagens,
    queryFn: getPorcentagens,
  });

  const dashData = data?.data;
  const porcentagensData = dataPorcentagens?.data;
  const isFetching = isFetchingTotalizadores || isFetchingPorcentagens;
  const isError = isErrorTotalizadores || isErrorPorcentagens;

  // Opções para os selects baseadas nos dados retornados
  const estadoOptions = useMemo(() => {
    if (!dashData?.por_uf) return [];
    return dashData.por_uf
      .filter(item => item.uf)
      .sort((a, b) => (a.uf || '').localeCompare(b.uf || ''))
      .map(item => ({
        value: item.uf || '',
        label: `${item.uf} (${item.quantidade})`,
      }));
  }, [dashData?.por_uf]);

  const municipioOptions = useMemo(() => {
    if (!dashData?.por_municipio) return [];
    let filteredMunicipios = dashData.por_municipio;

    if (tempEstado?.value) {
      filteredMunicipios = filteredMunicipios.filter(m => m.uf === tempEstado.value);
    }

    return filteredMunicipios
      .filter(item => item.cidade)
      .sort((a, b) => b.quantidade - a.quantidade)
      .map(item => ({
        value: item.cidade || '',
        label: `${item.cidade} - ${item.uf} (${item.quantidade})`,
      }));
  }, [dashData?.por_municipio, tempEstado]);

  const grupoOptions = useMemo(() => {
    if (!dashData?.por_grupo_empresas) return [];
    return dashData.por_grupo_empresas
      .filter(item => item.grupo)
      .sort((a, b) => b.quantidade - a.quantidade)
      .map(item => ({
        value: item.grupo,
        label: `${item.grupo} (${item.quantidade})`,
      }));
  }, [dashData?.por_grupo_empresas]);

  // Aplicar filtros
  const handleApplyFilters = () => {
    const newFilters: Record<string, string> = {
      ...Object.fromEntries(filters),
    };

    if (tempEstado?.value) {
      newFilters.uf = tempEstado.value;
    } else {
      delete newFilters.uf;
    }

    if (tempMunicipio?.value) {
      newFilters.cidade = tempMunicipio.value;
    } else {
      delete newFilters.cidade;
    }

    if (tempGrupo?.value) {
      newFilters.grupo_de_empresas = tempGrupo.value;
    } else {
      delete newFilters.grupo_de_empresas;
    }

    if (tempRegime?.value) {
      newFilters.tipo_regime = tempRegime.value;
    } else {
      delete newFilters.tipo_regime;
    }

    setFilters(createSearchParams(newFilters));
    setSelectedEstado(tempEstado?.value || null);
    setSelectedMunicipio(tempMunicipio?.value || null);
    setSelectedGrupo(tempGrupo?.value || null);
    setSelectedRegime(tempRegime?.value || null);
    onClose();
  };

  // Limpar filtros
  const handleClearFilters = (closeDrawer = false) => {
    setTempEstado(null);
    setTempMunicipio(null);
    setTempGrupo(null);
    setTempRegime(null);

    const newFilters: Record<string, string> = {
      ...Object.fromEntries(filters),
    };
    delete newFilters.uf;
    delete newFilters.cidade;
    delete newFilters.grupo_de_empresas;
    delete newFilters.tipo_regime;
    delete newFilters.filter_type;

    setFilters(createSearchParams(newFilters));
    setSelectedEstado(null);
    setSelectedMunicipio(null);
    setSelectedGrupo(null);
    setSelectedRegime(null);
    setEmpresasFilterType(null);

    if (closeDrawer) {
      onClose();
    }
  };

  // Abrir drawer e sincronizar estados temporários
  const handleOpenDrawer = () => {
    setTempEstado(selectedEstado ? { value: selectedEstado, label: selectedEstado } : null);
    setTempMunicipio(
      selectedMunicipio ? { value: selectedMunicipio, label: selectedMunicipio } : null,
    );
    const grupoOption = grupoOptions.find(g => g.value === selectedGrupo);
    setTempGrupo(grupoOption || null);
    const regimeOption = regimeOptions.find(r => r.value === selectedRegime);
    setTempRegime(regimeOption || null);
    onOpen();
  };

  // Verificar se há filtros ativos
  const hasActiveFilters =
    selectedEstado ||
    selectedMunicipio ||
    selectedGrupo ||
    selectedRegime ||
    filters.get('filter_type');

  // Função para aplicar filtro de estado ao clicar no mapa
  const handleStateClick = (uf: string | null) => {
    const newFilters: Record<string, string> = {
      ...Object.fromEntries(filters),
    };

    if (uf) {
      newFilters.uf = uf;
      delete newFilters.cidade;
    } else {
      delete newFilters.uf;
      delete newFilters.cidade;
    }

    setFilters(createSearchParams(newFilters));
    setSelectedEstado(uf);
    setSelectedMunicipio(null);
  };

  if (isFetching) {
    return (
      <Box>
        <PageHeader title="Dashboard - Empresas" />
        <Flex justify="center" align="center" minH="400px">
          <Spinner size="xl" color="blue.500" thickness="4px" />
        </Flex>
      </Box>
    );
  }

  if (isError || !dashData) {
    return (
      <Box>
        <PageHeader title="Dashboard - Empresas" />
        <Flex justify="center" align="center" minH="400px">
          <Text color="red.500">Erro ao carregar dados do dashboard</Text>
        </Flex>
      </Box>
    );
  }

  const regimeData = dashData.por_tipo_regime;
  const totalRegime = Object.values(regimeData).reduce((a, b) => a + b, 0);

  const getRegimePercent = (regimeKey: string): number | null => {
    if (!porcentagensData?.por_tipo_regime) return null;
    const found = porcentagensData.por_tipo_regime.find(r => r.regime === regimeKey);
    return found ? found.percentual : null;
  };

  const getGrupoPercent = (grupo: string): number | null => {
    if (!porcentagensData?.por_grupo_empresas) return null;
    const found = porcentagensData.por_grupo_empresas.find(g => g.grupo === grupo);
    return found ? found.percentual : null;
  };

  return (
    <Box pb={8}>
      <PageHeader title="Dashboard - Empresas" />

      {/* Filtros de Data e Botão de Filtros */}
      <Flex
        gap={3}
        mb={6}
        flexDirection={{ base: 'column', md: 'row' }}
        align={{ base: 'center', md: 'center' }}
      >
        <DateToolbarFilterImproved filters={filters} setFilters={setFilters} showMonthYearOnly />

        <Flex
          as="button"
          onClick={handleOpenDrawer}
          align="center"
          justify="center"
          gap={2}
          bg="white"
          shadow="sm"
          borderRadius="lg"
          px={4}
          h="42px"
          w={{ base: '100%', md: 'auto' }}
          cursor="pointer"
          _hover={{ bg: 'gray.50' }}
        >
          <Funnel size={16} color="#718096" />
          <Text fontWeight="medium" fontSize="sm" color="gray.700">
            Filtros
          </Text>
          {hasActiveFilters && (
            <Badge colorScheme="blue" borderRadius="full">
              {(selectedEstado ? 1 : 0) +
                (selectedMunicipio ? 1 : 0) +
                (selectedGrupo ? 1 : 0) +
                (selectedRegime ? 1 : 0) +
                (filters.get('filter_type') ? 1 : 0)}
            </Badge>
          )}
        </Flex>

        {hasActiveFilters && (
          <Flex
            as="button"
            onClick={() => handleClearFilters()}
            align="center"
            justify="center"
            gap={2}
            bg="white"
            shadow="sm"
            borderRadius="lg"
            px={4}
            h="42px"
            w={{ base: '100%', md: 'auto' }}
            cursor="pointer"
            _hover={{ bg: 'gray.50' }}
          >
            <XCircle size={16} color="#718096" />
            <Text fontWeight="medium" fontSize="sm" color="gray.700">
              Limpar
            </Text>
          </Flex>
        )}

        {hasActiveFilters && (
          <Flex gap={2} flexWrap="wrap" justify="center">
            {selectedEstado && (
              <Badge colorScheme="blue" fontSize="xs" px={2} py={1} borderRadius="md">
                UF: {selectedEstado}
              </Badge>
            )}
            {selectedMunicipio && (
              <Badge colorScheme="green" fontSize="xs" px={2} py={1} borderRadius="md">
                CIDADE: {selectedMunicipio}
              </Badge>
            )}
            {selectedGrupo && (
              <Badge colorScheme="purple" fontSize="xs" px={2} py={1} borderRadius="md">
                UNIDADE: {selectedGrupo}
              </Badge>
            )}
            {selectedRegime && (
              <Badge colorScheme="orange" fontSize="xs" px={2} py={1} borderRadius="md">
                REGIME:{' '}
                {regimeOptions.find(r => r.value === selectedRegime)?.label || selectedRegime}
              </Badge>
            )}
          </Flex>
        )}
      </Flex>

      {/* Drawer de Filtros */}
      <FiltersDrawer
        isOpen={isOpen}
        onClose={onClose}
        estadoOptions={estadoOptions}
        municipioOptions={municipioOptions}
        grupoOptions={grupoOptions}
        regimeOptions={regimeOptions}
        tempEstado={tempEstado}
        tempMunicipio={tempMunicipio}
        tempGrupo={tempGrupo}
        tempRegime={tempRegime}
        setTempEstado={setTempEstado}
        setTempMunicipio={setTempMunicipio}
        setTempGrupo={setTempGrupo}
        setTempRegime={setTempRegime}
        selectedEstado={selectedEstado}
        selectedMunicipio={selectedMunicipio}
        selectedGrupo={selectedGrupo}
        selectedRegime={selectedRegime}
        onApplyFilters={handleApplyFilters}
        onClearFilters={() => handleClearFilters(true)}
      />

      {/* Cards Principais */}
      <SimpleGrid columns={{ base: 2, sm: 2, md: 4, lg: 7 }} spacing={{ base: 3, md: 6 }} mb={8}>
        <StatCard
          title="Total de Empresas"
          value={dashData.total_empresas}
          percent={100}
          icon={Buildings}
          colorScheme="purple"
          isIncrease
        />
        <StatCard
          title="Ativas"
          value={dashData.qnt_emp_ativas_total}
          percent={dashData.qnt_emp_ativas_total_percent}
          icon={Buildings}
          colorScheme="green"
          isIncrease
          onClick={() => handleStatCardFilter('ativas')}
          onFilter={() => handleStatCardClick('ativas')}
          isFiltering={filters.get('filter_type') === 'ativas'}
        />
        <StatCard
          title="Ativas no Período"
          value={dashData.qnt_emp_ativas_periodo}
          percent={dashData.qnt_emp_ativas_percent_periodo}
          icon={Buildings}
          colorScheme="teal"
          isIncrease
          onClick={() => handleStatCardFilter('ativas_periodo')}
          onFilter={() => handleStatCardClick('ativas_periodo')}
          isFiltering={filters.get('filter_type') === 'ativas_periodo'}
        />
        <StatCard
          title="Inativas"
          value={dashData.qnt_emp_inativas}
          percent={dashData.qnt_emp_inativas_percent}
          icon={Buildings}
          colorScheme="gray"
          isIncrease={false}
          onClick={() => handleStatCardFilter('inativas')}
          onFilter={() => handleStatCardClick('inativas')}
          isFiltering={filters.get('filter_type') === 'inativas'}
        />
        <StatCard
          title="Inativas com Período Informado"
          value={dashData.qnt_emp_inativas_periodo}
          percent={dashData.qnt_emp_inativas_percent_periodo}
          icon={Buildings}
          colorScheme="red"
          isIncrease={false}
          onClick={() => handleStatCardFilter('inativas_periodo')}
          onFilter={() => handleStatCardClick('inativas_periodo')}
          isFiltering={filters.get('filter_type') === 'inativas_periodo'}
        />
        <StatCard
          title="Contratos Iniciados"
          value={dashData.qnt_entradas_periodo}
          percent={dashData.qnt_entradas_periodo_percent}
          icon={CaretUp}
          colorScheme="cyan"
          isIncrease
          onClick={() => handleStatCardFilter('entradas')}
          onFilter={() => handleStatCardClick('entradas')}
          isFiltering={filters.get('filter_type') === 'entradas'}
        />
        <StatCard
          title="Contratos Finalizados"
          value={dashData.qnt_saidas_periodo}
          percent={dashData.qnt_saidas_periodo_percent}
          icon={CaretDown}
          colorScheme="orange"
          isIncrease={false}
          onClick={() => handleStatCardFilter('saidas')}
          onFilter={() => handleStatCardClick('saidas')}
          isFiltering={filters.get('filter_type') === 'saidas'}
        />
        <StatCard
          title="Com Folha de Pagamento"
          value={dashData.qnt_com_folha_pagamento}
          percent={dashData.qnt_com_folha_pagamento_percent}
          icon={Users}
          colorScheme="blue"
          isIncrease
          onClick={() => handleStatCardFilter('com_folha')}
          onFilter={() => handleStatCardClick('com_folha')}
          isFiltering={filters.get('filter_type') === 'com_folha'}
        />
        <StatCard
          title="Fora do Acessórias"
          value={dashData.qnt_fora_do_acessorias ?? 0}
          percent={dashData.qnt_fora_do_acessorias_percent ?? 0}
          icon={Buildings}
          colorScheme="yellow"
          isIncrease={false}
          onClick={() => handleStatCardFilter('fora_do_acessorias')}
          onFilter={() => handleStatCardClick('fora_do_acessorias')}
          isFiltering={filters.get('filter_type') === 'fora_do_acessorias'}
        />
        <StatCard
          title="Fora da Domínio"
          value={dashData.qnt_fora_da_dominio ?? 0}
          percent={dashData.qnt_fora_da_dominio_percent ?? 0}
          icon={Buildings}
          colorScheme="pink"
          isIncrease={false}
          onClick={() => handleStatCardFilter('fora_da_dominio')}
          onFilter={() => handleStatCardClick('fora_da_dominio')}
          isFiltering={filters.get('filter_type') === 'fora_da_dominio'}
        />
      </SimpleGrid>

      {/* Faturamento */}
      <Grid
        templateColumns={{ base: '1fr', lg: '2fr 1fr' }}
        gap={{ base: 4, md: 6 }}
        mb={{ base: 4, md: 8 }}
      >
        <Box bg="white" p={{ base: 3, md: 6 }} borderRadius="xl" boxShadow="lg" position="relative">
          <Flex justify="space-between" align="center" mb={{ base: 2, md: 4 }}>
            {showFaturamentoChart && (
              <IconButton
                aria-label="Voltar"
                icon={<ArrowLeft size={18} />}
                size="sm"
                variant="ghost"
                onClick={() => setShowFaturamentoChart(false)}
              />
            )}
            <Heading
              size={{ base: 'sm', md: 'md' }}
              color="gray.700"
              flex={1}
              ml={showFaturamentoChart ? 2 : 0}
            >
              Faturamento Total
            </Heading>
            {!showFaturamentoChart && (
              <IconButton
                aria-label="Ver gráfico"
                icon={<ChartBar size={18} />}
                size="sm"
                variant="ghost"
                colorScheme="blue"
                onClick={() => setShowFaturamentoChart(true)}
              />
            )}
          </Flex>

          <Box position="relative" overflow="hidden" minH={{ base: '180px', md: '220px' }}>
            {/* View Cards */}
            <Box
              position={!showFaturamentoChart ? 'relative' : 'absolute'}
              top={0}
              left={0}
              width="100%"
              opacity={!showFaturamentoChart ? 1 : 0}
              transform={!showFaturamentoChart ? 'translateX(0)' : 'translateX(-100%)'}
              transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
              pointerEvents={!showFaturamentoChart ? 'auto' : 'none'}
            >
              {/* Linha de cima - 2 caixas centralizadas (Faturamento Bruto e Líquido) */}
              <Flex justify="center" gap={{ base: 2, md: 4 }} mb={{ base: 2, md: 4 }}>
                <Box
                  p={{ base: 2, md: 5 }}
                  bgGradient="linear(to-r, purple.400, purple.600)"
                  borderRadius="lg"
                  color="white"
                  textAlign="center"
                  flex={1}
                  minW={0}
                >
                  <Flex align="center" justify="center" gap={1} mb={1}>
                    <Icon as={CurrencyDollar} boxSize={{ base: 3, md: 5 }} />
                    <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" noOfLines={1}>
                      Faturamento Bruto
                    </Text>
                  </Flex>
                  <Text fontSize={{ base: 'sm', md: 'xl' }} fontWeight="bold">
                    {formatCurrency(dashData.total_faturamento_bruto)}
                  </Text>
                </Box>
                <Box
                  p={{ base: 2, md: 5 }}
                  bgGradient="linear(to-r, teal.400, teal.600)"
                  borderRadius="lg"
                  color="white"
                  textAlign="center"
                  flex={1}
                  minW={0}
                >
                  <Flex align="center" justify="center" gap={1} mb={1}>
                    <Icon as={CurrencyDollar} boxSize={{ base: 3, md: 5 }} />
                    <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" noOfLines={1}>
                      Faturamento Líquido
                    </Text>
                  </Flex>
                  <Text fontSize={{ base: 'sm', md: 'xl' }} fontWeight="bold">
                    {formatCurrency(dashData.total_faturamento_liquido)}
                  </Text>
                </Box>
              </Flex>

              {/* Linha de baixo - 3 caixas (Impostos, Serviços, Saídas) */}
              <SimpleGrid columns={3} spacing={{ base: 2, md: 4 }}>
                <Box
                  p={{ base: 2, md: 5 }}
                  bgGradient="linear(to-r, orange.400, orange.600)"
                  borderRadius="lg"
                  color="white"
                  textAlign="center"
                >
                  <Flex align="center" justify="center" gap={1} mb={1}>
                    <Icon
                      as={Receipt}
                      boxSize={{ base: 3, md: 5 }}
                      display={{ base: 'none', sm: 'block' }}
                    />
                    <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" noOfLines={1}>
                      Impostos
                    </Text>
                  </Flex>
                  <Text fontSize={{ base: 'sm', md: 'xl' }} fontWeight="bold">
                    {formatCurrency(dashData.total_impostos)}
                  </Text>
                </Box>
                <Box
                  p={{ base: 2, md: 5 }}
                  bgGradient="linear(to-r, green.400, green.600)"
                  borderRadius="lg"
                  color="white"
                  textAlign="center"
                >
                  <Flex align="center" justify="center" gap={1} mb={1}>
                    <Icon
                      as={Briefcase}
                      boxSize={{ base: 3, md: 5 }}
                      display={{ base: 'none', sm: 'block' }}
                    />
                    <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" noOfLines={1}>
                      Serviços
                    </Text>
                  </Flex>
                  <Text fontSize={{ base: 'sm', md: 'xl' }} fontWeight="bold">
                    {formatCurrency(dashData.total_servicos)}
                  </Text>
                </Box>
                <Box
                  p={{ base: 2, md: 5 }}
                  bgGradient="linear(to-r, blue.400, blue.600)"
                  borderRadius="lg"
                  color="white"
                  textAlign="center"
                >
                  <Flex align="center" justify="center" gap={1} mb={1}>
                    <Icon
                      as={Receipt}
                      boxSize={{ base: 3, md: 5 }}
                      display={{ base: 'none', sm: 'block' }}
                    />
                    <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" noOfLines={1}>
                      Saídas
                    </Text>
                  </Flex>
                  <Text fontSize={{ base: 'sm', md: 'xl' }} fontWeight="bold">
                    {formatCurrency(dashData.total_saidas)}
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>

            {/* View Gráfico */}
            <Box
              position={showFaturamentoChart ? 'relative' : 'absolute'}
              top={0}
              left={0}
              width="100%"
              opacity={showFaturamentoChart ? 1 : 0}
              transform={showFaturamentoChart ? 'translateX(0)' : 'translateX(100%)'}
              transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
              pointerEvents={showFaturamentoChart ? 'auto' : 'none'}
            >
              <Flex direction="column" gap={3}>
                <Box>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="sm" color="gray.600">
                      Total Faturamento Bruto
                    </Text>
                    <Text fontSize="sm" fontWeight="bold" color="purple.600">
                      {formatCurrency(dashData.total_faturamento_bruto)}
                    </Text>
                  </Flex>
                  <Progress
                    value={dashData.total_faturamento_bruto || 0.01}
                    max={
                      Math.max(
                        dashData.total_faturamento_bruto,
                        dashData.total_faturamento_liquido,
                        dashData.total_saidas,
                        dashData.total_servicos,
                        dashData.total_impostos,
                      ) || 1
                    }
                    colorScheme="purple"
                    size="md"
                    borderRadius="full"
                  />
                </Box>
                <Box>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="sm" color="gray.600">
                      Total Faturamento Líquido
                    </Text>
                    <Text fontSize="sm" fontWeight="bold" color="teal.600">
                      {formatCurrency(dashData.total_faturamento_liquido)}
                    </Text>
                  </Flex>
                  <Progress
                    value={dashData.total_faturamento_liquido || 0.01}
                    max={
                      Math.max(
                        dashData.total_faturamento_bruto,
                        dashData.total_faturamento_liquido,
                        dashData.total_saidas,
                        dashData.total_servicos,
                        dashData.total_impostos,
                      ) || 1
                    }
                    colorScheme="teal"
                    size="md"
                    borderRadius="full"
                  />
                </Box>
                <Box>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="sm" color="gray.600">
                      Total Impostos
                    </Text>
                    <Text fontSize="sm" fontWeight="bold" color="orange.600">
                      {formatCurrency(dashData.total_impostos)}
                    </Text>
                  </Flex>
                  <Progress
                    value={dashData.total_impostos || 0.01}
                    max={
                      Math.max(
                        dashData.total_faturamento_bruto,
                        dashData.total_faturamento_liquido,
                        dashData.total_saidas,
                        dashData.total_servicos,
                        dashData.total_impostos,
                      ) || 1
                    }
                    colorScheme="orange"
                    size="md"
                    borderRadius="full"
                  />
                </Box>
                <Box>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="sm" color="gray.600">
                      Total Serviços
                    </Text>
                    <Text fontSize="sm" fontWeight="bold" color="green.600">
                      {formatCurrency(dashData.total_servicos)}
                    </Text>
                  </Flex>
                  <Progress
                    value={dashData.total_servicos || 0.01}
                    max={
                      Math.max(
                        dashData.total_faturamento_bruto,
                        dashData.total_faturamento_liquido,
                        dashData.total_saidas,
                        dashData.total_servicos,
                        dashData.total_impostos,
                      ) || 1
                    }
                    colorScheme="green"
                    size="md"
                    borderRadius="full"
                  />
                </Box>
                <Box>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="sm" color="gray.600">
                      Total Saídas
                    </Text>
                    <Text fontSize="sm" fontWeight="bold" color="blue.600">
                      {formatCurrency(dashData.total_saidas)}
                    </Text>
                  </Flex>
                  <Progress
                    value={dashData.total_saidas || 0.01}
                    max={
                      Math.max(
                        dashData.total_faturamento_bruto,
                        dashData.total_faturamento_liquido,
                        dashData.total_saidas,
                        dashData.total_servicos,
                        dashData.total_impostos,
                      ) || 1
                    }
                    colorScheme="blue"
                    size="md"
                    borderRadius="full"
                  />
                </Box>
              </Flex>
            </Box>
          </Box>
        </Box>

        <Box bg="white" p={{ base: 3, md: 6 }} borderRadius="xl" boxShadow="lg">
          <Heading size={{ base: 'sm', md: 'md' }} mb={{ base: 2, md: 4 }} color="gray.700">
            Impostos Apurados
          </Heading>
          <SimpleGrid columns={{ base: 2, md: 2 }} spacing={{ base: 2, md: 3 }}>
            <TaxCard
              title="PIS"
              value={dashData.total_pis}
              color="blue.400"
              filterKey="pis"
              isFiltering={filters.get('filter_type') === 'pis'}
              onClick={() => handleTaxCardClick('pis')}
              onRemoveFilter={handleClearFilterType}
            />
            <TaxCard
              title="COFINS"
              value={dashData.total_cofins}
              color="green.400"
              filterKey="cofins"
              isFiltering={filters.get('filter_type') === 'cofins'}
              onClick={() => handleTaxCardClick('cofins')}
              onRemoveFilter={handleClearFilterType}
            />
            <TaxCard
              title="CSLL"
              value={dashData.total_csll}
              color="purple.400"
              filterKey="csll"
              isFiltering={filters.get('filter_type') === 'csll'}
              onClick={() => handleTaxCardClick('csll')}
              onRemoveFilter={handleClearFilterType}
            />
            <TaxCard
              title="IRPJ"
              value={dashData.total_irpj}
              color="orange.400"
              filterKey="irpj"
              isFiltering={filters.get('filter_type') === 'irpj'}
              onClick={() => handleTaxCardClick('irpj')}
              onRemoveFilter={handleClearFilterType}
            />
            <TaxCard
              title="ICMS"
              value={dashData.total_icms}
              color="red.400"
              filterKey="icms"
              isFiltering={filters.get('filter_type') === 'icms'}
              onClick={() => handleTaxCardClick('icms')}
              onRemoveFilter={handleClearFilterType}
            />
            <TaxCard
              title="ISS"
              value={dashData.total_iss_normal}
              color="cyan.400"
              filterKey="iss"
              isFiltering={filters.get('filter_type') === 'iss'}
              onClick={() => handleTaxCardClick('iss')}
              onRemoveFilter={handleClearFilterType}
            />
            <TaxCard
              title="ISS Retido"
              value={dashData.total_iss_retido}
              color="cyan.600"
              filterKey="iss_retido"
              isFiltering={filters.get('filter_type') === 'iss_retido'}
              onClick={() => handleTaxCardClick('iss_retido')}
              onRemoveFilter={handleClearFilterType}
            />
            <TaxCard
              title="Simples Nacional"
              value={dashData.vl_simples_nacional}
              color="teal.400"
              filterKey="simples_nacional"
              isFiltering={filters.get('filter_type') === 'simples_nacional'}
              onClick={() => handleTaxCardClick('simples_nacional')}
              onRemoveFilter={handleClearFilterType}
            />
          </SimpleGrid>
        </Box>
      </Grid>

      {/* Mapa e Regimes */}
      <Grid
        templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
        gap={{ base: 4, md: 6 }}
        mb={{ base: 4, md: 8 }}
      >
        <Box bg="white" p={{ base: 3, md: 6 }} borderRadius="xl" boxShadow="lg">
          <Heading size={{ base: 'sm', md: 'md' }} mb={{ base: 2, md: 4 }} color="gray.700">
            <Flex align="center" gap={2}>
              <Icon as={MapPin} boxSize={{ base: 4, md: 5 }} />
              Distribuição Geográfica
            </Flex>
          </Heading>
          <BrazilMap
            data={dashData.por_uf}
            municipioData={dashData.por_municipio}
            porcentagemUf={porcentagensData?.por_uf}
            porcentagemMunicipio={porcentagensData?.por_municipio}
            selectedUf={selectedEstado}
            onStateClick={handleStateClick}
          />
        </Box>

        <Box bg="white" p={{ base: 3, md: 6 }} borderRadius="xl" boxShadow="lg" position="relative">
          <Flex justify="space-between" align="center" mb={{ base: 2, md: 4 }}>
            {showRegimeChart && (
              <IconButton
                aria-label="Voltar"
                icon={<ArrowLeft size={18} />}
                size="sm"
                variant="ghost"
                onClick={() => setShowRegimeChart(false)}
              />
            )}
            <Heading size={{ base: 'sm', md: 'md' }} color="gray.700" flex={1}>
              <Flex align="center" gap={2} ml={showRegimeChart ? 2 : 0}>
                <Icon as={ChartPieSlice} boxSize={{ base: 4, md: 5 }} />
                Regime Tributário
              </Flex>
            </Heading>
            {!showRegimeChart && (
              <IconButton
                aria-label="Ver gráfico"
                icon={<ChartBar size={18} />}
                size="sm"
                variant="ghost"
                colorScheme="blue"
                onClick={() => setShowRegimeChart(true)}
              />
            )}
          </Flex>

          <Box position="relative" overflow="hidden" minH={{ base: '200px', md: '280px' }}>
            {/* View Barras */}
            <Box
              position={!showRegimeChart ? 'relative' : 'absolute'}
              top={0}
              left={0}
              width="100%"
              opacity={!showRegimeChart ? 1 : 0}
              transform={!showRegimeChart ? 'translateX(0)' : 'translateX(-100%)'}
              transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
              pointerEvents={!showRegimeChart ? 'auto' : 'none'}
            >
              {Object.entries(regimeData)
                .filter(([key]) => key !== 'outros')
                .sort((a, b) => b[1] - a[1])
                .map(([key, value]) => {
                  const percent =
                    getRegimePercent(key) ?? (totalRegime > 0 ? (value / totalRegime) * 100 : 0);
                  return (
                    <Box key={key} mb={4}>
                      <Flex justify="space-between" mb={1}>
                        <Text fontSize="sm" fontWeight="medium" color="gray.600">
                          {regimeLabels[key] || key}
                        </Text>
                        <Flex gap={2} align="center">
                          <Badge colorScheme="blue">{value}</Badge>
                          <Text fontSize="sm" color="gray.500">
                            {percent.toFixed(1)}%
                          </Text>
                        </Flex>
                      </Flex>
                      <Progress
                        value={percent}
                        size="sm"
                        borderRadius="full"
                        colorScheme={regimeColors[key]?.split('.')[0] || 'blue'}
                      />
                    </Box>
                  );
                })}
            </Box>

            {/* View Cards */}
            <Box
              position={showRegimeChart ? 'relative' : 'absolute'}
              top={0}
              left={0}
              width="100%"
              opacity={showRegimeChart ? 1 : 0}
              transform={showRegimeChart ? 'translateX(0)' : 'translateX(100%)'}
              transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
              pointerEvents={showRegimeChart ? 'auto' : 'none'}
            >
              <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                {Object.entries(regimeData)
                  .filter(([key]) => key !== 'outros')
                  .sort((a, b) => b[1] - a[1])
                  .filter(([, value]) => value > 0)
                  .map(([key, value]) => {
                    const percent =
                      getRegimePercent(key) ?? (totalRegime > 0 ? (value / totalRegime) * 100 : 0);
                    const isRegimeActive = filters.get('tipo_regime') === key;
                    return (
                      <Box
                        key={key}
                        p={4}
                        bg={`${regimeColors[key]?.split('.')[0] || 'blue'}.50`}
                        borderRadius="lg"
                        textAlign="center"
                        border={isRegimeActive ? '2px solid' : 'none'}
                        borderColor={
                          isRegimeActive ? regimeColors[key] || 'blue.400' : 'transparent'
                        }
                        borderLeft={isRegimeActive ? '2px solid' : '4px solid'}
                        borderLeftColor={regimeColors[key] || 'blue.400'}
                        boxShadow={isRegimeActive ? 'lg' : 'none'}
                        cursor="pointer"
                        transition="all 0.2s"
                        _hover={{
                          transform: 'scale(1.02)',
                          boxShadow: 'md',
                        }}
                        onClick={() => handleRegimeCardClick(key)}
                      >
                        <Text
                          fontSize="2xl"
                          fontWeight="bold"
                          color={regimeColors[key] || 'blue.400'}
                        >
                          {value}
                        </Text>
                        <Text fontSize="xs" fontWeight="medium" color="gray.600" noOfLines={1}>
                          {regimeLabels[key] || key}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {percent.toFixed(1)}%
                        </Text>
                      </Box>
                    );
                  })}
              </SimpleGrid>
            </Box>
          </Box>

          {/* Botão para limpar filtro de regime */}
          {selectedRegime && (
            <Flex justify="flex-end" position="absolute" bottom={3} right={3}>
              <Tooltip label="Limpar filtro de regime" placement="top" hasArrow>
                <IconButton
                  aria-label="Limpar filtro de regime"
                  icon={<XCircle size={24} weight="fill" />}
                  size="sm"
                  variant="ghost"
                  color="gray.400"
                  _hover={{ color: 'gray.600' }}
                  onClick={handleClearRegimeFilter}
                />
              </Tooltip>
            </Flex>
          )}
        </Box>
      </Grid>

      {/* Grupos e Top 10 Faturamento */}
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={{ base: 4, md: 6 }}>
        <Box bg="white" p={{ base: 3, md: 6 }} borderRadius="xl" boxShadow="lg">
          <Flex justify="space-between" align="center" mb={{ base: 2, md: 4 }}>
            {showGruposChart && (
              <IconButton
                aria-label="Voltar"
                icon={<ArrowLeft size={18} />}
                size="sm"
                variant="ghost"
                onClick={() => setShowGruposChart(false)}
              />
            )}
            <Heading size={{ base: 'sm', md: 'md' }} color="gray.700" flex={1}>
              <Flex align="center" gap={2} ml={showGruposChart ? 2 : 0}>
                <Icon as={Briefcase} boxSize={{ base: 4, md: 5 }} />
                Empresas por Grupo
              </Flex>
            </Heading>
            {!showGruposChart && (
              <IconButton
                aria-label="Ver gráfico"
                icon={<ChartBar size={18} />}
                size="sm"
                variant="ghost"
                colorScheme="blue"
                onClick={() => setShowGruposChart(true)}
              />
            )}
          </Flex>

          <Box position="relative" overflow="hidden" minH={{ base: '200px', md: '250px' }}>
            {/* View Tabela */}
            <Box
              position={!showGruposChart ? 'relative' : 'absolute'}
              top={0}
              left={0}
              width="100%"
              opacity={!showGruposChart ? 1 : 0}
              transform={!showGruposChart ? 'translateX(0)' : 'translateX(-100%)'}
              transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
              pointerEvents={!showGruposChart ? 'auto' : 'none'}
            >
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Grupo</Th>
                    <Th isNumeric>Quantidade</Th>
                    <Th isNumeric>%</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {dashData.por_grupo_empresas.map(grupo => {
                    const totalGrupos = dashData.por_grupo_empresas.reduce(
                      (sum, g) => sum + g.quantidade,
                      0,
                    );
                    const percent =
                      getGrupoPercent(grupo.grupo) ?? (grupo.quantidade / totalGrupos) * 100;
                    return (
                      <Tr key={grupo.grupo} _hover={{ bg: 'gray.50' }}>
                        <Td fontWeight="medium">{grupo.grupo}</Td>
                        <Td isNumeric>
                          <Badge colorScheme="blue">{grupo.quantidade}</Badge>
                        </Td>
                        <Td isNumeric color="gray.600">
                          {percent.toFixed(1)}%
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>

            {/* View Gráfico de barras */}
            <Box
              position={showGruposChart ? 'relative' : 'absolute'}
              top={0}
              left={0}
              width="100%"
              opacity={showGruposChart ? 1 : 0}
              transform={showGruposChart ? 'translateX(0)' : 'translateX(100%)'}
              transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
              pointerEvents={showGruposChart ? 'auto' : 'none'}
            >
              <Flex direction="column" gap={3}>
                {dashData.por_grupo_empresas.map(grupo => {
                  const totalGrupos = dashData.por_grupo_empresas.reduce(
                    (sum, g) => sum + g.quantidade,
                    0,
                  );
                  const percent =
                    getGrupoPercent(grupo.grupo) ?? (grupo.quantidade / totalGrupos) * 100;
                  const maxQtd = Math.max(...dashData.por_grupo_empresas.map(g => g.quantidade));
                  return (
                    <Box key={grupo.grupo}>
                      <Flex justify="space-between" mb={1}>
                        <Text fontSize="sm" color="gray.600" noOfLines={1}>
                          {grupo.grupo}
                        </Text>
                        <Flex gap={2} align="center">
                          <Badge colorScheme="blue">{grupo.quantidade}</Badge>
                          <Text fontSize="xs" color="gray.500">
                            {percent.toFixed(1)}%
                          </Text>
                        </Flex>
                      </Flex>
                      <Progress
                        value={grupo.quantidade}
                        max={maxQtd}
                        colorScheme="blue"
                        size="md"
                        borderRadius="full"
                      />
                    </Box>
                  );
                })}
              </Flex>
            </Box>
          </Box>
        </Box>

        <Box bg="white" p={{ base: 3, md: 6 }} borderRadius="xl" boxShadow="lg">
          <Heading size={{ base: 'sm', md: 'md' }} mb={{ base: 2, md: 4 }} color="gray.700">
            <Flex align="center" gap={2}>
              <Icon as={TrendUp} boxSize={{ base: 4, md: 5 }} />
              Top 10 Faturamento
            </Flex>
          </Heading>
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <Th>Empresa</Th>
                  <Th isNumeric>Faturamento</Th>
                </Tr>
              </Thead>
              <Tbody>
                {dashData.top_10_faturamento.map((empresa, index) => (
                  <Tr key={empresa.id} _hover={{ bg: 'gray.50' }}>
                    <Td>
                      <Badge
                        colorScheme={index < 3 ? 'yellow' : 'gray'}
                        variant={index < 3 ? 'solid' : 'subtle'}
                      >
                        {index + 1}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                        {empresa.nome}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatCNPJ(empresa.cnpj)}
                      </Text>
                    </Td>
                    <Td isNumeric>
                      <Text fontWeight="bold" color="green.600">
                        {formatCurrency(empresa.total_faturamento)}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </Grid>

      {/* Permanência Média e Aniversários */}
      <Grid
        templateColumns={{ base: '1fr', lg: '1fr 2fr' }}
        gap={{ base: 4, md: 6 }}
        mb={{ base: 4, md: 8 }}
        mt={{ base: 4, md: 8 }}
        alignItems="stretch"
      >
        <PermanenciaMediaCard />
        <AniversariosEmpresasCard />
      </Grid>

      {/* Listagem Geral de Empresas */}
      <EmpresasListSection
        ref={empresasListRef}
        filters={filtersObject}
        filterType={empresasFilterType}
        onClearFilterType={handleClearFilterType}
      />
    </Box>
  );
}

export default EmpresaDashboardPage;
